import React from 'react';
import { number, oneOf, string, bool } from 'prop-types';
import { dt } from '../../helpers/Utils/GetTailwindToken';
import YdtStars from '../../assets/svg/StarsNew.svg';
import Icon from '../Icon/Icon';

const Rating = ({
    automationId,
    averageRating,
    ratingIconSize,
    totalRatings,
    wrapperClassName,
    showCount,
    showZeroRatings,
    iconWrapperStyle,
}) => {
    const isSmallIcon = ratingIconSize === 'small';
    const iconFontSizeClass = isSmallIcon ? dt(['text-xsmall']) : dt(['text-small']);
    const iconSize = isSmallIcon ? 16 : 20;
    const isRating = totalRatings > 0 && (averageRating > 0 || showZeroRatings);
    const averageRatingLimit = 5;

    const averageRatingTitle = `Average Rating: ${averageRating} out of ${averageRatingLimit}`.toLocaleString();
    const totalRatingLabel =
        totalRatings === 1
            ? `Total Rating: ${totalRatings}`.toLocaleString()
            : `Total Ratings: ${totalRatings}`.toLocaleString();

    const config = {
        ratingIcon: {
            averageRating: {
                className: dt(['absolute', 'overflow-hidden']),
                style: {
                    left: '0',
                    top: '0',
                    width:
                        averageRating <= averageRatingLimit
                            ? `${(averageRating * 100) / averageRatingLimit}%`
                            : '100%',
                },
                title: averageRatingTitle,
            },
            icon: {
                height: `${iconSize}`,
                iconType: 'svg',
                name: 'ydt-stars',
                iconComponent: <YdtStars />,
                viewBox: `0 0 ${iconSize * averageRatingLimit} ${iconSize}`,
                width: `${iconSize * averageRatingLimit}`,
            },
            wrapper: {
                className: `${dt(['mr-2', 'relative'])} ${iconWrapperStyle}`,
                style: { lineHeight: '0' },
            },
        },
        ratingTotal: {
            className: dt(['underline', 'text-black', 'text-small']),
            title: totalRatingLabel,
        },
        wrapper: {
            'data-automation-id': automationId,
            className: `${dt([
                'flex',
                'flex-wrap',
                'items-center',
            ])} ${iconFontSizeClass} ${wrapperClassName}`,
        },
    };

    const { ratingIcon, ratingTotal, wrapper } = config;

    const RatingIcon = () => (
        <div {...ratingIcon.wrapper}>
            <Icon className={dt(['text-gray-15'])} {...ratingIcon.icon}>
                <YdtStars />
            </Icon>
            <div {...ratingIcon.averageRating}>
                <Icon className={dt(['text-black'])} {...ratingIcon.icon}>
                    <YdtStars />
                </Icon>
            </div>
        </div>
    );

    const RatingTotal = () => <span {...ratingTotal}>{totalRatings.toLocaleString()}</span>;

    return (
        isRating && (
            <div {...wrapper}>
                <RatingIcon />
                {showCount && <RatingTotal />}
            </div>
        )
    );
};

Rating.propTypes = {
    /** Average rating value. */
    averageRating: number.isRequired,
    /** Total number of ratings. */
    totalRatings: number.isRequired,
    /** Automation testing ID. */
    automationId: string,
    /** Size of the icon. */
    ratingIconSize: oneOf(['small', 'large']),
    /** Wrapper class name(s). */
    wrapperClassName: string,
    /** to show and hide the rating count */
    showCount: bool,
    /** to show zero ratings */
    showZeroRatings: bool,
    iconWrapperStyle: string,
};

Rating.defaultProps = {
    automationId: null,
    ratingIconSize: 'large',
    wrapperClassName: '',
    showCount: false,
    showZeroRatings: false,
    iconWrapperStyle: '',
};

export default Rating;
